// Sandboxes in the console (LO-190): what keeps them out of every list, what a
// request says about one, and what an install says about the hosts it may call.
//
// Plain functions and no React, so the rules are testable without a browser and
// the screens that use them stay about layout.

/**
 * What a sandbox instance's `app` is on the control plane.
 *
 * `services/control_api/handler.py` names it `SANDBOX_APP` and gives it to every
 * sandbox instance on purpose: every reader that filters instances by app (the
 * QA runner, the auto-update sweep, this console's "is it already installed"
 * check) then passes over a sandbox without knowing it exists. The control
 * plane's own `GET /instances` already leaves them out, and this is the second
 * line: if one ever reached a screen it would be read as an install, and
 * approving the app it tests would then skip installing the real one.
 */
export const SANDBOX_APP = "sandbox";

/** Is this control-plane row a sandbox's rather than an install's. */
export function isSandboxInstance(row) {
  return Boolean(row) && (row.app === SANDBOX_APP || Boolean(row.sandbox));
}

/** `rows` without a sandbox's. Safe on anything a read did not return. */
export function withoutSandboxes(rows) {
  return Array.isArray(rows) ? rows.filter((row) => !isSandboxInstance(row)) : [];
}

// -- approved egress ------------------------------------------------------------

/** Said when a manifest approves no host. A fact about the approval, not a gap. */
export const NO_EGRESS = "no outbound hosts approved";

/**
 * What the Apps screen cannot say about the hosts it lists.
 *
 * Worded as what the screen *states*, so it stays true whether or not anything
 * enforces the list: the screen reads the approved manifest and has no view of
 * a function's network. Enforcement (LO-180) is a different claim on a
 * different surface, and this sentence neither makes it nor waits for it.
 */
export const EGRESS_DOES_NOT_PROVE =
  "These are the hosts that were approved for this app's server function. "
  + "This screen states what was approved, not what is blocked: it does not "
  + "show that a call to any other host is stopped, or that these were ever called.";

/** The hosts a manifest approved, as the kernel published them. */
export function approvedEgress(app) {
  const hosts = Array.isArray(app?.egress)
    ? app.egress.map((h) => String(h)).filter(Boolean)
    : [];
  return { hosts, none: hosts.length === 0 };
}

// -- a request's sandbox ----------------------------------------------------------

/**
 * The request states in which a sandbox exists: the kernel's own
 * `sandbox.OPEN_STATES` (`kernel/lopie_kernel/sandbox.py`). A sandbox is torn
 * down when its request is approved, rejected, cancelled or replaced, so in
 * any other state the control plane has nothing to say and asking is a request
 * that can only come back 404 (which the browser prints as a console error on
 * every visit). The queue never lists a draft, since a draft is its author's,
 * but the set is the kernel's and not a narrower one of this screen's.
 */
export const OPEN_STATES = ["draft", "pending"];

/**
 * Does this request have a sandbox worth asking the control plane about.
 *
 * Only a request in an open state, and only one that names a build or that the
 * kernel has a client pointed at. The decided history does not need a round
 * trip each, and a sandbox is torn down with its request, so there is nothing
 * to read for one. Shared by the control-plane read below and the three reads
 * the approver's view makes (LO-185), so the two can never disagree about which
 * requests have one.
 *
 * A closed request can still carry a `sandbox` the kernel annotated (a leftover
 * that teardown missed). That is shown, from the kernel's own pointer, as stale
 * because the request is closed (`sandboxState`); it is not a reason to ask the
 * control plane, whose answer could not change what is said.
 */
export function wantsSandbox(request) {
  return OPEN_STATES.includes(request?.state)
    && Boolean(request?.proposed?.build_id || request?.sandbox);
}

/**
 * What to keep of a failed control-plane read of one request's sandbox.
 *
 * `null` for a 404: the control plane says this request has no sandbox (yet, or
 * ever), which is an answer and not a fault, and the request is simply shown
 * with whatever the kernel says of it. Anything else (a refusal, a 5xx, no
 * network at all) is not "no sandbox", and is kept as `{ unreadable }` so the
 * card can say the control plane did not answer instead of saying nothing.
 */
export function controlFailure(error) {
  if (error?.status === 404) return null;
  const status = Number.isInteger(error?.status) ? error.status : 0;
  return { unreadable: { status } };
}

/** The clause that says why the control plane's answer is missing. */
function unreadableWhy(control) {
  const status = control?.unreadable?.status;
  return status ? `The control plane answered HTTP ${status}.` : "The control plane could not be reached.";
}

export const STATES = ["provisioning", "seeding", "ready", "stale", "failed", "unknown"];

const STALE_WHY = {
  build: "The request now names a different build from the one this sandbox was made from.",
  ceiling: "The request's ceiling has changed since this sandbox was pointed at it, so it is testing a ceiling nobody is asking for now.",
  closed: "The request is no longer open, and a sandbox was meant to be removed with it.",
};

/**
 * What to say about one request's sandbox, or `null` when there is nothing to say.
 *
 * Two sources, because two systems know different halves and neither knows the
 * other's: the kernel's request view carries `sandbox` (is a client pointed at
 * this request, and is that pointer still for the request's current build and
 * ceiling), and the control plane's `GET /sandboxes/{request}` carries whether
 * anything is up and seeded. `control` is that body, `undefined` while it has
 * not been asked, and `null` when it could not be read or said there is none.
 *
 *  - **stale** wins over everything: a sandbox for another build or ceiling is
 *    running something that is not what is being approved, whatever its health.
 *  - **provisioning**, **seeding**, **ready** are the control plane's, in order:
 *    the instance is not ready; it is ready and not yet seeded; both.
 *  - **failed** is neither of those and must not read as in progress.
 *  - **unknown** is the kernel knowing a client is pointed and the control
 *    plane not answering. It is said as that: a pointer does not say a function
 *    is running, so "running" is not claimed.
 */
export function sandboxState(request, control) {
  const pointer = request?.sandbox || null;
  const wanted = request?.proposed?.build_id || "";
  const live = control?.current || null;

  if (live && wanted && live.buildId && live.buildId !== wanted) {
    return build("stale", live, STALE_WHY.build);
  }
  if (pointer && pointer.pointed && !pointer.fresh) {
    return build("stale", live, STALE_WHY[pointer.stale] || "The kernel no longer treats this sandbox as being for the request as it stands.");
  }
  if (live) {
    const seed = live.seed || {};
    if (live.status === "failed") {
      return build("failed", live, live.message
        ? `The instance did not come up: ${live.message}`
        : "The instance did not come up.");
    }
    if (live.ready) {
      return build("ready", live, "The instance is up and seeded for this build.");
    }
    if (live.status !== "ready") {
      return build("provisioning", live, live.message
        ? `The instance is being built: ${live.message}`
        : "The instance is being built. It takes ten minutes or so.");
    }
    if (seed.state === "failed" || seed.error) {
      return build("failed", live, `The instance is up and seeding failed${seed.error ? `: ${seed.error}` : "."}`);
    }
    return build("seeding", live, "The instance is up and its synthetic data is being written.");
  }
  if (pointer && pointer.pointed) {
    return build("unknown", null,
      "The kernel has a sandbox pointed at this build. The control plane did not "
      + "say whether anything is running, so this does not claim it."
      + (control?.unreadable ? ` ${unreadableWhy(control)}` : ""));
  }
  if (control?.unreadable) {
    // No pointer, and the one other source failed in a way that is not "there
    // is none": say that it could not be read, and not that a sandbox exists.
    return build("unknown", null,
      `${unreadableWhy(control)} Whether this request has a sandbox is not known, `
      + "and this does not say that it has one.");
  }
  return null;
}

function build(state, live, detail) {
  const appUrl = state === "unknown" || state === "failed" ? "" : String(live?.appUrl || "");
  return {
    state,
    running: ["provisioning", "seeding", "ready", "stale"].includes(state),
    label: labelFor(state),
    detail,
    appUrl,
    name: String(live?.name || ""),
  };
}

function labelFor(state) {
  if (state === "failed") return "sandbox failed";
  if (state === "unknown") return "sandbox · state not readable";
  return `sandbox running · ${state}`;
}
